import React, { useState } from 'react'
import { supabase } from '../supabase'
import { useAuth } from '../context/AuthContext'
import {
  GST_RATE, fmtINR, fmtInvDate, calcInvoice, invoiceDbFields, useAmountPaid, overpaidMessage,
  normStatus, invDate, printInvoice, sendInvoiceWhatsApp,
} from '../lib/billing'
import { friendlyError } from '../lib/permissions'

// Invoice generator used from the Appointments and Patients profile panels.
// The invoice number is assigned by the database; Print/WhatsApp become available
// once the invoice is saved so they always show the real number.

const T = {
  teal950: '#082828', teal900: '#0d3d3d', teal800: '#0f5454',
  teal700: '#126868', teal600: '#148080', teal500: '#19a8a8',
  teal400: '#2ecece', teal200: '#90e0e0', teal100: '#ccf2f2', teal50: '#edfafa',
  white: '#ffffff',
  gray50: '#f8fafc', gray100: '#f1f5f9', gray200: '#e2e8f0',
  gray300: '#cbd5e1', gray400: '#94a3b8', gray500: '#64748b',
  gray600: '#475569', gray700: '#334155', gray900: '#0f172a',
  red50: '#fff1f2', red600: '#dc2626',
  font: "'Segoe UI', system-ui, -apple-system, sans-serif",
}

export const INVOICE_SERVICES = [
  { name: 'Consultation',           rate: 500   },
  { name: 'Follow-up Consultation', rate: 300   },
  { name: 'Chemical Peel',          rate: 2000  },
  { name: 'HydraFacial',            rate: 3000  },
  { name: 'PRP Treatment',          rate: 5000  },
  { name: 'Laser Treatment',        rate: 4000  },
  { name: 'Microneedling',          rate: 3500  },
  { name: 'Botox',                  rate: 8000  },
  { name: 'Dermal Filler',          rate: 12000 },
  { name: 'Acne Treatment',         rate: 1500  },
  { name: 'Skin Brightening',       rate: 2500  },
  { name: 'Mole Removal',           rate: 1000  },
  { name: 'Wart Removal',           rate: 800   },
  { name: 'Scalp Treatment',        rate: 2000  },
  { name: 'Patch Test',             rate: 500   },
  { name: 'Other',                  rate: 0     },
]

const PAYMENT_MODES = ['Cash', 'UPI', 'Card', 'Bank Transfer', 'Insurance']

const STATUS_STYLE = {
  paid:    { bg: '#dcfce7', color: '#16a34a' },
  partial: { bg: '#fef3c7', color: '#d97706' },
  unpaid:  { bg: '#fee2e2', color: '#dc2626' },
}

const sx = {
  width: '100%', padding: '9px 12px', border: `1.5px solid ${T.gray200}`,
  borderRadius: 8, fontSize: 13.5, color: T.gray900, background: T.gray50,
  outline: 'none', fontFamily: T.font, boxSizing: 'border-box',
}
const focusOn  = e => { e.target.style.borderColor = T.teal500; e.target.style.boxShadow = `0 0 0 3px ${T.teal100}` }
const focusOff = e => { e.target.style.borderColor = T.gray200; e.target.style.boxShadow = 'none' }
function FLabel({ children }) {
  return <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: T.gray500, marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.4px' }}>{children}</label>
}

function todayLocal() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

export default function InvoiceModal({ patient, appointmentId, existingInvoices, onClose, onSaved }) {
  const { userProfile, clinic } = useAuth()
  const pd       = patient || {}
  const patName  = pd.full_name || 'Patient'

  const [services,   setServices]   = useState([{ name: '', qty: 1, rate: '' }])
  const [invDateVal, setInvDate]    = useState(todayLocal())
  const [discount,   setDiscount]   = useState('')
  const [discType,   setDiscType]   = useState('flat') // 'flat' | '%'
  const [gstEnabled, setGstEnabled] = useState(false)  // clinic is not GST-registered — default OFF
  const [payMode,    setPayMode]    = useState('Cash')
  const [saving,     setSaving]     = useState(false)
  const [error,      setError]      = useState('')
  const [pastOpen,   setPastOpen]   = useState(false)
  const [saved,      setSaved]      = useState(null)   // invoice row returned by the DB

  // Totals without payment first, so amount-paid can default to the final total
  const base = calcInvoice({ lines: services, discountType: discType, discountValue: discount, gstEnabled, amountPaid: 0 })
  const amt  = useAmountPaid(base.total)
  const calc = calcInvoice({ lines: services, discountType: discType, discountValue: discount, gstEnabled, amountPaid: amt.value })

  function addRow()        { setServices(s => [...s, { name: '', qty: 1, rate: '' }]) }
  function updRow(i, patch) { setServices(s => s.map((r, j) => j === i ? { ...r, ...patch } : r)) }
  function remRow(i)       { setServices(s => s.filter((_, j) => j !== i)) }

  async function handleSave() {
    if (!calc.items.length) { setError('Please add at least one service.'); return }
    if (calc.overpaid)      { setError(overpaidMessage(calc)); return }
    setError(''); setSaving(true)
    const { data, error: err } = await supabase.from('invoices').insert({
      patient_id:     pd.id,
      patient_name:   pd.full_name || null,
      patient_phone:  pd.phone || null,
      appointment_id: appointmentId || null,
      clinic_id:      userProfile?.clinic_id,
      ...invoiceDbFields(calc, { date: invDateVal || todayLocal(), paymentMode: payMode }),
    }).select('*').single()
    setSaving(false)
    if (err) { setError(friendlyError(err)); return }
    setSaved(data)
    onSaved && onSaved(data)
  }

  const thSx    = { fontSize: 10, fontWeight: 700, color: T.gray500, textTransform: 'uppercase', letterSpacing: '.4px' }
  const cellInp = { ...sx, fontSize: 12.5, padding: '6px 8px' }
  const st      = STATUS_STYLE[calc.status]
  const contact = [clinic?.phone, clinic?.email].filter(Boolean).join(' · ')

  return (
    <div onClick={e => { if (e.target === e.currentTarget) onClose() }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(8,40,40,0.7)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px 20px', fontFamily: T.font }}>
      <div style={{ background: T.white, borderRadius: 16, width: '100%', maxWidth: 700, maxHeight: '95vh', display: 'flex', flexDirection: 'column', boxShadow: '0 28px 80px rgba(0,0,0,.4)', overflow: 'hidden' }}>

        {/* ── Clinic header (from clinics table) ── */}
        <div style={{ background: T.teal950, padding: '14px 20px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: T.white, letterSpacing: '-.3px', marginBottom: 3 }}>{clinic?.name || 'New Invoice'}</div>
            {clinic?.tagline && <div style={{ fontSize: 11.5, fontWeight: 400, color: T.teal200, marginTop: -2, marginBottom: 3 }}>{clinic.tagline}</div>}
            {(clinic?.address || contact) && (
              <div style={{ fontSize: 10.5, color: 'rgba(255,255,255,.5)', lineHeight: 1.5 }}>
                {clinic?.address}{clinic?.address && contact ? <br /> : null}{contact}
              </div>
            )}
          </div>
          <button onClick={onClose} style={{ width: 30, height: 30, borderRadius: 8, background: 'rgba(255,255,255,.12)', border: 'none', color: T.white, fontSize: 18, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>×</button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '18px 20px' }}>
          {saved ? (
            /* ── Saved state: show DB-assigned number, allow print / WhatsApp ── */
            <div style={{ textAlign: 'center', padding: '24px 10px' }}>
              <div style={{ fontSize: 40, marginBottom: 8 }}>✅</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: T.gray900 }}>Invoice saved</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: T.teal700, margin: '6px 0 14px' }}>{saved.invoice_number}</div>
              <div style={{ display: 'inline-grid', gridTemplateColumns: 'auto auto', gap: '6px 24px', textAlign: 'left', fontSize: 13.5, color: T.gray600 }}>
                <span>Patient</span><strong style={{ color: T.gray900 }}>{saved.patient_name || patName}</strong>
                <span>Total</span><strong style={{ color: T.gray900 }}>{fmtINR(saved.total)}</strong>
                <span>Paid</span><strong style={{ color: T.gray900 }}>{fmtINR(saved.amount_paid)}</strong>
                <span>Balance</span><strong style={{ color: Number(saved.balance_due) > 0 ? T.red600 : '#16a34a' }}>{fmtINR(saved.balance_due)}</strong>
              </div>
            </div>
          ) : (
            <>
              {error && <div style={{ background: T.red50, border: '1px solid #fecdd3', borderRadius: 8, padding: '10px 14px', color: T.red600, fontSize: 13, marginBottom: 14 }}>⚠️ {error}</div>}

              {/* Invoice # (assigned on save) and Date */}
              <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
                <div style={{ flex: 1 }}>
                  <FLabel>Invoice Number</FLabel>
                  <div style={{ ...sx, color: T.gray400, fontStyle: 'italic' }}>Assigned automatically on save</div>
                </div>
                <div style={{ flex: 1 }}>
                  <FLabel>Invoice Date</FLabel>
                  <input type="date" value={invDateVal} onChange={e => setInvDate(e.target.value)} style={sx} onFocus={focusOn} onBlur={focusOff} />
                </div>
              </div>

              {/* Patient row */}
              <div style={{ display: 'flex', gap: 12, padding: '10px 14px', background: T.teal50, borderRadius: 9, border: `1px solid ${T.teal100}`, marginBottom: 16 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 9.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px', color: T.teal600, marginBottom: 2 }}>Patient</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: T.gray900 }}>{patName}</div>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 9.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px', color: T.teal600, marginBottom: 2 }}>Phone</div>
                  <div style={{ fontSize: 13.5, fontWeight: 500, color: T.gray700 }}>{pd.phone || '—'}</div>
                </div>
              </div>

              {/* Services table */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 7 }}>
                  <FLabel>Services &amp; Procedures</FLabel>
                  <button onClick={addRow} style={{ fontSize: 11.5, color: T.teal600, background: T.teal50, border: `1px solid ${T.teal200}`, borderRadius: 6, padding: '3px 10px', cursor: 'pointer', fontWeight: 700, fontFamily: T.font }}>+ Add Row</button>
                </div>
                <div style={{ border: `1px solid ${T.gray200}`, borderRadius: 9, overflow: 'hidden' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 54px 94px 90px 28px', background: T.teal950, padding: '7px 10px', gap: 6 }}>
                    {['Service / Procedure', 'Qty', 'Rate (₹)', 'Amount (₹)', ''].map((h, idx) => (
                      <div key={idx} style={{ ...thSx, color: 'rgba(255,255,255,.6)', textAlign: idx >= 2 && idx < 4 ? 'right' : 'left' }}>{h}</div>
                    ))}
                  </div>
                  {services.map((row, i) => {
                    const lineAmt = (parseFloat(row.qty) || 0) * (parseFloat(row.rate) || 0)
                    return (
                      <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 54px 94px 90px 28px', padding: '5px 10px', gap: 6, borderTop: `1px solid ${T.gray100}`, alignItems: 'center', background: i % 2 === 1 ? T.gray50 : T.white }}>
                        <select value={row.name}
                          onChange={e => {
                            const svc = INVOICE_SERVICES.find(s => s.name === e.target.value)
                            updRow(i, { name: e.target.value, rate: svc ? svc.rate : row.rate })
                          }}
                          style={{ ...cellInp, cursor: 'pointer' }} onFocus={focusOn} onBlur={focusOff}>
                          <option value="">Select service…</option>
                          {INVOICE_SERVICES.map(s => <option key={s.name} value={s.name}>{s.name}</option>)}
                        </select>
                        <input type="number" value={row.qty} min="1" onChange={e => updRow(i, { qty: e.target.value })} style={{ ...cellInp, textAlign: 'center' }} />
                        <input type="number" value={row.rate} min="0" placeholder="0" onChange={e => updRow(i, { rate: e.target.value })} style={{ ...cellInp, textAlign: 'right' }} />
                        <div style={{ fontSize: 13, fontWeight: 600, color: T.gray900, textAlign: 'right', paddingRight: 2 }}>{fmtINR(lineAmt)}</div>
                        <button onClick={() => remRow(i)} disabled={services.length === 1}
                          style={{ background: 'none', border: 'none', color: services.length === 1 ? T.gray200 : '#dc2626', cursor: services.length === 1 ? 'default' : 'pointer', fontSize: 17, lineHeight: 1, fontFamily: T.font }}>×</button>
                      </div>
                    )
                  })}
                </div>
              </div>

              {/* Totals */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
                <div style={{ width: 300, background: T.gray50, borderRadius: 10, padding: '14px 16px', border: `1px solid ${T.gray200}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: T.gray600, marginBottom: 8 }}>
                    <span>Subtotal</span><span style={{ fontWeight: 600 }}>{fmtINR(calc.subtotal)}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                    <span style={{ fontSize: 12, color: T.gray500, whiteSpace: 'nowrap' }}>Discount</span>
                    <input type="number" min="0" value={discount} onChange={e => setDiscount(e.target.value)} placeholder="0"
                      style={{ ...sx, fontSize: 12, padding: '4px 7px', flex: 1 }} />
                    <select value={discType} onChange={e => setDiscType(e.target.value)}
                      style={{ ...sx, fontSize: 12, padding: '4px 6px', width: 60, cursor: 'pointer' }}>
                      <option value="flat">₹</option>
                      <option value="%">%</option>
                    </select>
                    <span style={{ fontSize: 12, color: '#dc2626', fontWeight: 600, whiteSpace: 'nowrap', minWidth: 64, textAlign: 'right' }}>
                      {calc.discount > 0 ? `−${fmtINR(calc.discount)}` : '—'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 12.5, color: T.gray600, fontWeight: 500 }}>
                      <input type="checkbox" checked={gstEnabled} onChange={e => setGstEnabled(e.target.checked)}
                        style={{ width: 15, height: 15, cursor: 'pointer', accentColor: T.teal600 }} />
                      GST ({GST_RATE}%)
                    </label>
                    <span style={{ fontSize: 12, color: '#0369a1', fontWeight: 600 }}>{gstEnabled ? fmtINR(calc.gst) : '—'}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 16, fontWeight: 800, color: T.teal900, borderTop: `2px solid ${T.teal800}`, paddingTop: 10 }}>
                    <span>Total Payable</span><span>{fmtINR(calc.total)}</span>
                  </div>
                </div>
              </div>

              {/* Payment */}
              <div style={{ background: T.white, border: `1px solid ${T.gray200}`, borderRadius: 10, padding: '14px 16px', marginBottom: 12 }}>
                <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.7px', color: T.teal700, marginBottom: 12 }}>Payment</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                  <div>
                    <FLabel>Payment Mode</FLabel>
                    <select value={payMode} onChange={e => setPayMode(e.target.value)} style={{ ...sx, cursor: 'pointer' }} onFocus={focusOn} onBlur={focusOff}>
                      {PAYMENT_MODES.map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </div>
                  <div>
                    <FLabel>Amount Paid (₹)</FLabel>
                    <input type="number" min="0" max={calc.total} value={amt.value} onChange={e => amt.set(e.target.value)}
                      style={{ ...sx, borderColor: calc.overpaid ? '#fca5a5' : T.gray200 }} onFocus={focusOn} onBlur={focusOff} />
                    {amt.manual && (
                      <button type="button" onClick={amt.resetToTotal} style={{ marginTop: 4, fontSize: 11, color: T.teal600, background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: T.font }}>
                        ↺ Use full amount ({fmtINR(calc.total)})
                      </button>
                    )}
                  </div>
                </div>
                {calc.overpaid && (
                  <div style={{ fontSize: 12, color: T.red600, fontWeight: 600, marginBottom: 10 }}>⚠️ {overpaidMessage(calc)}</div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderRadius: 8, background: calc.balance > 0 ? '#fff1f2' : '#f0fdf4' }}>
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px', color: T.gray400 }}>Balance Due</div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: calc.balance > 0 ? '#dc2626' : '#16a34a', marginTop: 2 }}>{fmtINR(calc.balance)}</div>
                  </div>
                  <span style={{ padding: '5px 14px', borderRadius: 20, fontSize: 12, fontWeight: 800, letterSpacing: '.5px', background: st.bg, color: st.color }}>
                    {calc.status.toUpperCase()}
                  </span>
                </div>
              </div>
            </>
          )}

          {/* Past invoices — collapsible */}
          {(existingInvoices || []).length > 0 && (
            <div style={{ border: `1px solid ${T.gray200}`, borderRadius: 10, overflow: 'hidden', marginBottom: 4 }}>
              <button onClick={() => setPastOpen(o => !o)}
                style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: T.gray50, border: 'none', cursor: 'pointer', fontFamily: T.font }}>
                <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.6px', color: T.teal700 }}>
                  Past Invoices ({existingInvoices.length})
                </span>
                <span style={{ fontSize: 11, color: T.gray400, transform: pastOpen ? 'rotate(90deg)' : 'none', transition: 'transform .2s', display: 'inline-block' }}>▶</span>
              </button>
              {pastOpen && existingInvoices.map(inv => {
                const s = normStatus(inv)
                return (
                  <div key={inv.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px', borderTop: `1px solid ${T.gray100}` }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12.5, fontWeight: 700, color: T.teal700 }}>{inv.invoice_number || inv.id?.slice(0, 8)}</div>
                      <div style={{ fontSize: 11, color: T.gray500, marginTop: 1 }}>{fmtInvDate(invDate(inv))}</div>
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: T.gray900 }}>{fmtINR(inv.total)}</div>
                    <span style={{ padding: '3px 9px', borderRadius: 10, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', background: STATUS_STYLE[s].bg, color: STATUS_STYLE[s].color }}>{s}</span>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div style={{ padding: '11px 20px', borderTop: `1px solid ${T.gray200}`, display: 'flex', gap: 8, alignItems: 'center', background: T.gray50, flexShrink: 0 }}>
          {saved ? (
            <>
              <button onClick={() => sendInvoiceWhatsApp(saved, clinic, pd)}
                style={{ padding: '8px 14px', borderRadius: 8, border: '1.5px solid #22c55e', background: '#dcfce7', color: '#166534', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: T.font }}>
                💬 WhatsApp Bill
              </button>
              <button onClick={() => printInvoice(saved, clinic, pd)}
                style={{ padding: '8px 14px', borderRadius: 8, border: `1.5px solid ${T.gray200}`, background: T.white, color: T.gray600, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: T.font }}>
                🖨️ Print / PDF
              </button>
              <button onClick={onClose}
                style={{ marginLeft: 'auto', padding: '8px 24px', borderRadius: 8, border: 'none', background: T.teal600, color: T.white, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: T.font }}>
                Done
              </button>
            </>
          ) : (
            <>
              <span style={{ fontSize: 11.5, color: T.gray400 }}>Print &amp; WhatsApp are available after saving.</span>
              <button onClick={onClose}
                style={{ marginLeft: 'auto', padding: '8px 18px', borderRadius: 8, border: `1.5px solid ${T.gray200}`, background: T.white, color: T.gray600, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: T.font }}>
                Cancel
              </button>
              <button onClick={handleSave} disabled={saving || calc.overpaid}
                style={{ padding: '8px 24px', borderRadius: 8, border: 'none', background: saving || calc.overpaid ? T.teal200 : T.teal600, color: T.white, fontSize: 13, fontWeight: 700, cursor: saving || calc.overpaid ? 'not-allowed' : 'pointer', fontFamily: T.font }}>
                {saving ? 'Saving…' : 'Save Invoice'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
